import { getCart, isCartShaped } from "@theme/cart-state";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { sectionRenderer } from "@theme/section-renderer";

const OPT_OUT_KEY = "pebble:tier_opt_out";
const SOURCE = "shipping-protection";
const TOGGLE_SELECTOR = "#shipping-protection-toggle";

let tierConfigCache;
let reconciling = false;

function getTierConfig() {
  if (tierConfigCache !== undefined) return tierConfigCache;
  const node = document.getElementById("tier-cart-config");
  if (!node || !node.dataset.tierConfig) {
    tierConfigCache = null;
    return null;
  }
  try {
    const thresholds = JSON.parse(node.dataset.tierConfig);
    if (!Array.isArray(thresholds) || thresholds.length === 0) {
      tierConfigCache = null;
      return null;
    }
    thresholds.sort((a, b) => (a.min_total_cents || 0) - (b.min_total_cents || 0));
    tierConfigCache = { handle: node.dataset.productHandle, thresholds };
    return tierConfigCache;
  } catch (e) {
    console.warn("[tier-cart] invalid tier config JSON", e);
    tierConfigCache = null;
    return null;
  }
}

function isOptedOut() {
  try { return localStorage.getItem(OPT_OUT_KEY) === "1"; } catch (e) { return false; }
}
function setOptedOut(value) {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, "1");
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch (e) {}
}

function pickTierVariantId(baseTotalCents, thresholds) {
  let pick = null;
  for (let i = 0; i < thresholds.length; i++) {
    if (baseTotalCents >= (thresholds[i].min_total_cents || 0)) {
      pick = thresholds[i];
    } else {
      break;
    }
  }
  return pick ? String(pick.variant_id) : null;
}

function collectCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll("cart-items-component").forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * Reads `item_count` out of a rendered cart section. The section already
 * computes the tier-adjusted count server-side (see `cart-drawer.liquid`), so
 * this is authoritative and free — no extra round-trip — whenever a mutation
 * response carried sections back.
 *
 * @param {Record<string, string> | null | undefined} sections
 * @returns {number | null}
 */
function itemCountFromSections(sections) {
  if (!sections) return null;

  for (const html of Object.values(sections)) {
    const doc = new DOMParser().parseFromString(String(html ?? ""), "text/html");
    const el = doc.querySelector('[ref="cartItemCount"]') || doc.querySelector(".cart-bubble__text-count");
    if (!el?.textContent) continue;

    const count = parseInt(el.textContent, 10);
    if (Number.isFinite(count)) return count;
  }

  return null;
}

async function cartUpdateByKeys(keys, quantity, sectionIds) {
  const updates = {};
  for (const k of keys) updates[k] = quantity;
  const body = { updates };
  if (sectionIds.length) {
    body.sections = sectionIds.join(",");
    body.sections_url = window.location.pathname;
  }
  const res = await fetch(FoxTheme.routes.cart_update_url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function cartAdd(variantId, sectionIds) {
  const body = { items: [{ id: variantId, quantity: 1 }] };
  if (sectionIds.length) {
    body.sections = sectionIds.join(",");
    body.sections_url = window.location.pathname;
  }
  const res = await fetch(FoxTheme.routes.cart_add_url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}

function syncCheckboxes() {
  const checked = !isOptedOut();
  document.querySelectorAll(TOGGLE_SELECTOR).forEach((el) => {
    if (el instanceof HTMLInputElement) el.checked = checked;
  });
}

function setCheckoutDisabled(disabled) {
  document.querySelectorAll("#checkout, .cart-summary__checkout-button").forEach((el) => {
    if (el instanceof HTMLButtonElement) el.disabled = disabled;
  });
}

function visibleItemCount(cart, tierHandle) {
  let count = cart.item_count || 0;
  for (const it of cart.items || []) {
    if (it.handle === tierHandle) count -= it.quantity;
  }
  return Math.max(0, count);
}

async function reconcile(cart, { silent = false } = {}) {
  const config = getTierConfig();
  if (!config || !config.handle) return;
  if (reconciling) return;
  reconciling = true;

  try {
    if (!cart) cart = await getCart();

    const tierLines = cart.items.filter((it) => it.handle === config.handle);
    const nonTierItems = cart.items.filter((it) => it.handle !== config.handle);

    let baseTotal = 0;
    for (const it of nonTierItems) {
      baseTotal += typeof it.final_line_price === "number" ? it.final_line_price : it.line_price;
    }
    const nonTierQty = nonTierItems.reduce((s, it) => s + it.quantity, 0);

    const targetVariantId =
      nonTierQty > 0 && !isOptedOut() ? pickTierVariantId(baseTotal, config.thresholds) : null;

    const correctState =
      (targetVariantId === null && tierLines.length === 0) ||
      (targetVariantId !== null &&
        tierLines.length === 1 &&
        tierLines[0].quantity === 1 &&
        String(tierLines[0].variant_id) === targetVariantId);

    if (correctState) return;

    const sectionIds = collectCartSectionIds();
    let lastResponse = null;

    if (tierLines.length > 0) {
      lastResponse = await cartUpdateByKeys(tierLines.map((l) => l.key), 0, sectionIds);
    }

    if (targetVariantId) {
      lastResponse = await cartAdd(targetVariantId, sectionIds);
    }

    if (silent) {
      const ids = collectCartSectionIds();
      await Promise.all(ids.map((id) => sectionRenderer.renderSection(id, { cache: false })));
      return;
    }

    const sections = lastResponse && lastResponse.sections ? lastResponse.sections : null;

    // `/cart/update.js` returns a full cart, but `/cart/add.js` returns only the
    // lines it just added — so when the last mutation was the tier add,
    // `lastResponse` is NOT the cart. Passing it on made `visibleItemCount()`
    // read `undefined` for `item_count` and dispatch 0, blanking the cart badge.
    let finalCart = isCartShaped(lastResponse) ? lastResponse : null;

    // Re-read once when the tier add was the last mutation, so downstream
    // listeners get a real cart as the event resource instead of the added line.
    // This is the request the add response can't stand in for; it replaces the
    // one `cart-items` would otherwise make on receiving the event.
    if (!finalCart) finalCart = await getCart({ force: true });

    document.dispatchEvent(
      new CartUpdateEvent(finalCart, "", {
        source: SOURCE,
        // Prefer the count the section already computed server-side (it applies
        // the same tier exclusion as visibleItemCount).
        itemCount: itemCountFromSections(sections) ?? visibleItemCount(finalCart, config.handle),
        sections: sections || undefined,
      })
    );
  } catch (e) {
    console.error("[tier-cart] reconcile failed", e);
  } finally {
    reconciling = false;
  }
}

async function onCartUpdate(event) {
  // Dispatcher already ran reconcileTierInline before morphing — nothing left
  // to do here besides resync the toggles.
  if (event?.detail?.data?.tierHandled) {
    requestAnimationFrame(syncCheckboxes);
    return;
  }
  if (event?.detail?.data?.source === SOURCE || reconciling) {
    requestAnimationFrame(syncCheckboxes);
    return;
  }

  try {
    // Reuse the cart shape already on the event so we skip an initial /cart.js
    // fetch. The reactive path is now the backstop for dispatchers (add-to-cart,
    // discount, gift wrap) that haven't been migrated to the inline path.
    const preloaded = event?.detail?.resource;
    const cart = preloaded && Array.isArray(preloaded.items) ? preloaded : null;
    await reconcile(cart);
  } catch (e) {
    console.error("[tier-cart] cart update handler failed", e);
  } finally {
    requestAnimationFrame(syncCheckboxes);
  }
}

/**
 * Inline tier reconcile: takes a parsed cart-mutation response, performs any
 * tier add/remove needed for the new subtotal, and returns the final response
 * (with merged sections) so the caller can morph once. Returns input unchanged
 * when no tier change is required, when tier config is missing, or when input
 * isn't shaped like a cart response.
 *
 * Designed to be awaited between fetch and morph in cart mutation paths
 * (cart-items.js updateQuantity, etc.). Pair with `data.tierHandled: true` on
 * the subsequent CartUpdateEvent so the reactive listener skips a duplicate
 * reconcile.
 *
 * @param {object} parsedResponse - JSON from /cart/change.js, /cart/add.js, or /cart/update.js
 * @param {string[]} [sectionIdsHint] - Section IDs the caller already asked for
 * @returns {Promise<object>}
 */
export async function reconcileTierInline(parsedResponse, sectionIdsHint) {
  const config = getTierConfig();
  if (!config?.handle) return parsedResponse;
  if (!parsedResponse || !Array.isArray(parsedResponse.items)) return parsedResponse;
  if (reconciling) return parsedResponse;

  const cart = parsedResponse;
  const tierLines = cart.items.filter((it) => it.handle === config.handle);
  const nonTierItems = cart.items.filter((it) => it.handle !== config.handle);

  let baseTotal = 0;
  for (const it of nonTierItems) {
    baseTotal += typeof it.final_line_price === "number" ? it.final_line_price : it.line_price;
  }
  const nonTierQty = nonTierItems.reduce((s, it) => s + it.quantity, 0);

  const targetVariantId =
    nonTierQty > 0 && !isOptedOut() ? pickTierVariantId(baseTotal, config.thresholds) : null;

  const correctState =
    (targetVariantId === null && tierLines.length === 0) ||
    (targetVariantId !== null &&
      tierLines.length === 1 &&
      tierLines[0].quantity === 1 &&
      String(tierLines[0].variant_id) === targetVariantId);

  if (correctState) return parsedResponse;

  const sectionIds =
    sectionIdsHint && sectionIdsHint.length ? sectionIdsHint : collectCartSectionIds();

  reconciling = true;
  try {
    let lastResponse = parsedResponse;

    if (tierLines.length > 0) {
      lastResponse = await cartUpdateByKeys(tierLines.map((l) => l.key), 0, sectionIds);
    }
    if (targetVariantId) {
      lastResponse = await cartAdd(targetVariantId, sectionIds);
    }

    requestAnimationFrame(syncCheckboxes);

    // Merge sections so callers that asked for sections the tier mutation didn't
    // re-request still see them in the returned response.
    const mergedSections = {
      ...(parsedResponse.sections || {}),
      ...(lastResponse.sections || {}),
    };

    // Only let `lastResponse` supply the cart fields when it actually is a cart.
    // `/cart/add.js` returns just the added lines, so spreading it here replaced
    // `items` with the single tier line — callers reading the merged response
    // then saw a one-line cart. Sections are authoritative either way, so fall
    // back to the caller's own (pre-tier) cart fields rather than a wrong shape.
    const cartFields = isCartShaped(lastResponse) ? lastResponse : parsedResponse;

    return { ...parsedResponse, ...cartFields, sections: mergedSections };
  } catch (e) {
    console.error("[tier-cart] inline reconcile failed", e);
    return parsedResponse;
  } finally {
    reconciling = false;
  }
}

async function onToggleChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches(TOGGLE_SELECTOR)) return;
  setOptedOut(!target.checked);
  syncCheckboxes();
  setCheckoutDisabled(true);
  try {
    await reconcile();
  } catch (e) {
    console.error("[tier-cart] toggle reconcile failed", e);
  } finally {
    setCheckoutDisabled(false);
  }
}

(async function init() {
  if (!getTierConfig()) return;
  try {
    const cart = await getCart();
    await reconcile(cart, { silent: true });
  } catch (e) {
    console.error("[tier-cart] init failed", e);
  }
  syncCheckboxes();
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  document.addEventListener("change", onToggleChange);
})();
